/**
 * Block-based email template schema shared between client (builder UI),
 * server (validation + send-time compile) and the future Python voice
 * agent (which will pick a template by id and render it before
 * enqueuing into `mail/`).
 *
 * Design choices:
 *   • Blocks are flat, ordered. No infinite nesting — keeps the UI
 *     trivial and the email HTML guaranteed-table-safe.
 *   • Each block type carries everything it needs for the renderer
 *     (no implicit theme bag), so adding a new block is local work.
 *   • A small set of theme tokens (`bgColor`, `cardColor`, `width`,
 *     `rounded`) lives at the template root and applies to the wrapper.
 *
 * Renderers consume `MailTemplate` and produce:
 *   • email-HTML (nested-table layout, inline styles) for the actual
 *     send via `mail/`
 *   • a preview React tree for the builder
 */
import { z } from 'zod';
// ── Block primitives ────────────────────────────────────────────────────
const Align = z.enum(['left', 'center', 'right']);
const HexColor = z
    .string()
    .regex(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i, 'Hex-Farbe wie #4A90E2');
export const MailBlockSchema = z.discriminatedUnion('type', [
    z.object({
        id: z.string().min(1),
        type: z.literal('heading'),
        text: z.string().max(280),
        level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
        align: Align.default('left'),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('paragraph'),
        text: z.string().max(2000),
        align: Align.default('left'),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('button'),
        text: z.string().max(80),
        url: z.string().max(2048),
        align: Align.default('center'),
        bgColor: HexColor.default('#0f172a'),
        color: HexColor.default('#ffffff'),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('image'),
        url: z.string().max(2048),
        alt: z.string().max(200).default(''),
        width: z.number().int().min(40).max(1200).default(560),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('hero'),
        title: z.string().max(140),
        subtitle: z.string().max(280).default(''),
        bgColor: HexColor.default('#0f172a'),
        color: HexColor.default('#ffffff'),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('divider'),
        color: HexColor.default('#e5e7eb'),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('spacer'),
        height: z.number().int().min(4).max(120).default(24),
    }),
    z.object({
        id: z.string().min(1),
        type: z.literal('columns2'),
        left: z.string().max(800).default(''),
        right: z.string().max(800).default(''),
    }),
]);
// ── Template root ───────────────────────────────────────────────────────
export const MailTemplateSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    subject: z.string().min(1).max(200),
    /** Optional plain-text fallback. When empty, server derives from blocks. */
    preheader: z.string().max(200).default(''),
    blocks: z.array(MailBlockSchema).max(50),
    // Theme — used by the wrapper around the blocks, not per-block
    bgColor: HexColor.default('#f4f6f9'),
    cardColor: HexColor.default('#ffffff'),
    width: z.number().int().min(320).max(800).default(600),
    rounded: z.boolean().default(true),
    /** Reserved for future variant tagging (`'transactional' | 'marketing'`). */
    category: z.string().max(40).default('transactional'),
    // Multi-channel content — same template doc carries the SMS/WhatsApp
    // variants so a single "Vorlage" can be selected for any channel.
    // Both empty = template applies to email only (legacy shape).
    /** WhatsApp body. Plain text with WhatsApp-style markup: `*bold*`,
     *  `_italic_`, `~strike~`, `~~~mono~~~`, and merge tags. Max 4096
     *  chars (WhatsApp Business hard limit). */
    whatsappBody: z.string().max(4000).default(''),
    /** SMS body. Plain text only — no formatting. Max 1600 chars
     *  (10 segments × 160). The send-time helper will refuse anything
     *  beyond that and ask the operator to shorten or switch to email. */
    smsBody: z.string().max(1600).default(''),
});
/** Subset accepted by the upsert callable — server fills `id`/`updatedAt`. */
export const MailTemplateUpsertSchema = MailTemplateSchema.extend({
    id: z.string().min(1).max(64).optional(),
});
// ── Default block factories (builder helpers) ──────────────────────────
let _seq = 0;
const _id = () => {
    _seq += 1;
    return `b${Date.now().toString(36)}_${_seq}`;
};
export function makeBlock(type) {
    switch (type) {
        case 'heading':
            return { id: _id(), type, text: 'Überschrift', level: 2, align: 'left' };
        case 'paragraph':
            return { id: _id(), type, text: 'Text-Block. Klick zum Bearbeiten.', align: 'left' };
        case 'button':
            return {
                id: _id(),
                type,
                text: 'Mehr erfahren',
                url: 'https://',
                align: 'center',
                bgColor: '#0f172a',
                color: '#ffffff',
            };
        case 'image':
            return { id: _id(), type, url: '', alt: '', width: 560 };
        case 'hero':
            return {
                id: _id(),
                type,
                title: 'Willkommen',
                subtitle: 'Kurze Begrüßungs-Zeile.',
                bgColor: '#0f172a',
                color: '#ffffff',
            };
        case 'divider':
            return { id: _id(), type, color: '#e5e7eb' };
        case 'spacer':
            return { id: _id(), type, height: 24 };
        case 'columns2':
            return { id: _id(), type, left: 'Linke Spalte', right: 'Rechte Spalte' };
    }
}
//# sourceMappingURL=mailTemplates.js.map